/**
=========================================================
* Trust Cargo - shop code to supplier
=========================================================

* Every order's shop code is also a supplier reference, so adding an order
* has to leave a supplier of the same code behind - HIY06 the order, HIY06
* the supplier - ready to book purchases against.
*
* The rule belongs on the server, and it is there: OrderController calls
* Supplier::ensureForCode() inside the same transaction that writes the
* order. That version is the reliable one, because it also covers orders
* created by anything other than this screen.
*
* This is the same rule repeated in the browser so it works against a server
* that has not been updated yet. The two do not fight: when the server has
* already created the supplier, the check below finds it and does nothing.
*/

import axios from "axios";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * Create the supplier for a shop code unless one already holds that code.
 *
 * Never throws. A failure here must not make a successfully created order
 * look like it failed - the order is the thing that matters, and a missing
 * supplier can be added by hand.
 *
 * Returns "created" | "exists" | "failed", which the caller may report.
 */
export default async function ensureSupplierForShopCode(shopCode) {
  const code = String(shopCode || "")
    .trim()
    .toUpperCase();

  if (!code) return "failed";

  try {
    const res = await fetch(`${API}/suppliers`, { cache: "no-store" });
    if (!res.ok) return "failed";

    const suppliers = await res.json();

    if (
      Array.isArray(suppliers) &&
      suppliers.some((s) => String(s.code || "").toUpperCase() === code)
    ) {
      return "exists";
    }

    // nothing else is known about them yet; the name is filled in later
    await axios.post(
      `${API}/suppliers`,
      { code, name: code },
      { headers: { Accept: "application/json", "Content-Type": "application/json" } }
    );

    return "created";
  } catch (e) {
    // 422 here means the code was taken between the check and the write,
    // which is the outcome we wanted anyway
    if (e?.response?.status === 422) return "exists";
    return "failed";
  }
}
